// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { useQuery, keepPreviousData, queryOptions, skipToken } from '@tanstack/vue-query'

// Models
import { Site, SiteID } from '../../sites/models/site'
import { Pagination } from '../../common/models/pagination'
import { Employee, EmployeeID } from '../models/employee'

// Services
import { EligibleSearchForm, EmployeeSearchForm, EmployeeSortBy, fetch, fetchEligible, HasNewAppLoginFilter, LastActiveStatus, show } from '../employeeService'
import { DateTime } from 'luxon'
import { AreaID } from '../../areas/models/area'
import { ShiftID } from '../../shifts/models/shift'
import { Skill } from '../../skills/models/skill'
import { Building } from '../../buildings/models/building'
import { Group } from '../../groups/models/group'

export interface EmployeeFilters {
    name?: MaybeRefOrGetter<string>
    skill?: MaybeRefOrGetter<Skill | null>

    group?: MaybeRefOrGetter<Group | null>

    building?: MaybeRefOrGetter<Building | null>
    role?: MaybeRefOrGetter<string | undefined>
    hasAccount?: MaybeRefOrGetter<'all' | 'yes' | 'no'>
    hasNewAppLogin?: MaybeRefOrGetter<HasNewAppLoginFilter | null>
    lastActiveStatus?: MaybeRefOrGetter<LastActiveStatus | null>

    showArchived?: MaybeRefOrGetter<boolean>

    sortBy?: MaybeRefOrGetter<EmployeeSortBy | null>
    sortDirection?: MaybeRefOrGetter<'asc' | 'desc'>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>

    status?: MaybeRefOrGetter<'all' | 'applied' | 'pending'>
}

interface EligibleFilters {
    skill?: MaybeRefOrGetter<Skill | null>

    group?: MaybeRefOrGetter<Group | null>

    shiftId?: MaybeRefOrGetter<ShiftID | null | undefined>
    areaId?: MaybeRefOrGetter<AreaID | null | undefined>
    startDate?: MaybeRefOrGetter<DateTime | null | undefined>
    endDate?: MaybeRefOrGetter<DateTime | null | undefined>

    name?: MaybeRefOrGetter<string>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>

    status?: MaybeRefOrGetter<'applied'>
}

export const employeeKeys = {
    all: ['employees'] as const,
    lists: () => [...employeeKeys.all, 'list'] as const,
    list: (filters: EmployeeFilters) => [...employeeKeys.lists(), {
        name: filters.name,
        building: filters.building,
        skill: filters.skill,
        group: filters.group,
        roles: filters.role,
        hasAccount: filters.hasAccount,
        hasNewAppLogin: filters.hasNewAppLogin,
        lastActiveStatus: filters.lastActiveStatus,
        showArchived: filters.showArchived,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        perPage: filters.perPage,
        page: filters.page
    }] as const,
    details: () => [...employeeKeys.all, 'detail'] as const,
    detail: (id: MaybeRefOrGetter) => [...employeeKeys.details(), id] as const,
    checkArchive: (id: MaybeRefOrGetter) => [...employeeKeys.details(), id, 'checkArchive'] as const,
    listEligibleForShift: (filters: EligibleFilters) => [...employeeKeys.lists(), { name: filters.name, skill: filters.skill, group: filters.group, shiftId: filters.shiftId, areaId: filters.areaId, startDate: filters.startDate, endDate: filters.endDate }] as const,
    listByShift: (shiftId: MaybeRefOrGetter<ShiftID>, filters: EmployeeFilters) => [...employeeKeys.lists(), shiftId, { name: filters.name, perPage: filters.perPage, page: filters.page, status: filters.status }] as const,
}

export function useEmployeeQuery(siteId: MaybeRefOrGetter<SiteID>, filters: EmployeeFilters = {}) {
    return useQuery<{ employees: Employee[], pagination: Pagination }, Error>(employeeQueryOptions(siteId, filters))
}

export function employeeQueryOptions(siteId: MaybeRefOrGetter<SiteID>, filters: EmployeeFilters) {
    return queryOptions<{ employees: Employee[], pagination: Pagination }, Error>({
        queryKey: employeeKeys.list(filters),
        queryFn: async () => {
            const form: EmployeeSearchForm = {
                name: toValue(filters.name),
                sortBy: toValue(filters.sortBy),
                sortDirection: toValue(filters.sortDirection),
                perPage: toValue(filters.perPage) ?? 15,
                page: toValue(filters.page) ?? 1,
            }

            const building = toValue(filters.building)
            if (building) {
                form.buildingId = building.id
            }

            const skill = toValue(filters.skill)
            if (skill) {
                form.skillId = skill.id
            }

            const group = toValue(filters.group)
            if (group) {
                form.groupId = group.id
            }

            const role = toValue(filters.role)
            if (role && role !== 'all') {
                form.role = role
            }

            const hasAccount = toValue(filters.hasAccount)
            if (hasAccount) {
                form.hasAccount = hasAccount
            }

            const lastActiveStatus = toValue(filters.lastActiveStatus)
            if (lastActiveStatus) {
                form.lastActiveStatus = lastActiveStatus
            }

            const showArchived = toValue(filters.showArchived)
            if (showArchived) {
                form.showArchived = showArchived
            }

            const hasNewAppLogin = toValue(filters.hasNewAppLogin)
            if (hasNewAppLogin) {
                form.hasNewAppLogin = hasNewAppLogin
            }

            return await fetch(toValue(siteId), form)
        },
        placeholderData: keepPreviousData,
    })
}

export function useEmployeeDetailQuery(site: MaybeRefOrGetter<Site>, employeeId: MaybeRefOrGetter<EmployeeID | null>) {
    const employeeIdValue = toValue(employeeId)

    return useQuery({
        queryKey: employeeKeys.detail(employeeId),
        queryFn: employeeIdValue ? async () => {
            return show(toValue(site), employeeIdValue)
        } : skipToken,
    })
}

export function useEligibleQuery(siteId: MaybeRefOrGetter<SiteID>, filters: EligibleFilters) {
    return useQuery<{ employees: Employee[], pagination: Pagination }, Error>(eligibleQueryOptions(siteId, filters))
}

export function eligibleQueryOptions(siteId: MaybeRefOrGetter<SiteID>, filters: EligibleFilters) {
    return queryOptions<{ employees: Employee[], pagination: Pagination }, Error>({
        queryKey: employeeKeys.listEligibleForShift(filters),
        queryFn: async () => {
            const startDate = toValue(filters.startDate)
            const endDate = toValue(filters.endDate)
            const form: EligibleSearchForm = {
                shiftId: toValue(filters.shiftId),
                areaId: toValue(filters.areaId),
                startDate: startDate ?? null,
                endDate: endDate ?? null,

                perPage: toValue(filters.perPage) ?? 15,
                page: toValue(filters.page) ?? 1,
            }

            const skill = toValue(filters.skill)
            if (skill) {
                form.skillId = skill.id
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            const group = toValue(filters.group)
            if (group) {
                form.groupId = group.id
            }

            return await fetchEligible(toValue(siteId), form)
        },
        placeholderData: keepPreviousData,
    })
}